/**
 * App toast helpers — one place for auth / MFA copy and post-redirect flashes.
 * Visuals live in `@w7/ui` ToastProvider (top-right, slide-in, progress bar).
 */

import { useCallback } from 'react'
import { useToast, type ToastTone } from '@w7/ui'

const FLASH_KEY = 'w7.toast.flash'

export type ToastFlash = {
  tone: ToastTone
  message: string
  detail?: string
}

/** Survives full-page redirects (OIDC logout → /login, callback → app). */
export function flashToast(toast: ToastFlash): void {
  try {
    sessionStorage.setItem(FLASH_KEY, JSON.stringify(toast))
  } catch {
    /* private mode / quota — toast is best-effort */
  }
}

export function consumeToastFlash(): ToastFlash | null {
  try {
    const raw = sessionStorage.getItem(FLASH_KEY)
    if (!raw) return null
    sessionStorage.removeItem(FLASH_KEY)
    return JSON.parse(raw) as ToastFlash
  } catch {
    sessionStorage.removeItem(FLASH_KEY)
    return null
  }
}

export const toastCopy = {
  loginSuccess: {
    tone: 'success' as const,
    message: 'Signed in successfully',
    detail: 'Welcome back.',
  },
  logoutSuccess: {
    tone: 'success' as const,
    message: 'Signed out successfully',
    detail: 'Your session has ended.',
  },
  loginExpired: {
    tone: 'warn' as const,
    message: 'Session expired',
    detail: 'Sign in again to continue.',
  },
  sessionIdle: {
    tone: 'warn' as const,
    message: 'Signed out due to inactivity',
    detail: 'No activity for 30 minutes. Sign in again to continue.',
  },
  sessionMax: {
    tone: 'warn' as const,
    message: 'Session time limit reached',
    detail: 'Login sessions last 1 hour. Sign in again to continue.',
  },
  loginDenied: {
    tone: 'warn' as const,
    message: 'Sign-in cancelled',
    detail: 'No session was created.',
  },
  loginError: {
    tone: 'error' as const,
    message: 'Sign-in failed',
    detail: 'Try again, or use demo access if available.',
  },
  passwordChanged: {
    tone: 'success' as const,
    message: 'Password updated',
    detail: 'Use the new password the next time you sign in.',
  },
  passwordCancelled: {
    tone: 'info' as const,
    message: 'Password change cancelled',
    detail: 'Your existing password is unchanged.',
  },
  passwordChangeIncomplete: {
    tone: 'warn' as const,
    message: 'Password was not changed',
    detail:
      'Keycloak did not finish UPDATE_PASSWORD. Re-seed local IdP (npm run keycloak:seed) or try again.',
  },
} as const

/** Typed helpers on top of `useToast` — prefer this in features. */
export function useAppToast() {
  const toast = useToast()

  const show = useCallback(
    (item: ToastFlash) => {
      toast.push(item)
    },
    [toast],
  )

  return {
    ...toast,
    show,
    loginSuccess: () => show(toastCopy.loginSuccess),
    logoutSuccess: () => show(toastCopy.logoutSuccess),
  }
}
